import { Button } from '@repo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/card'
import { useT } from '@repo/ui/hooks'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { Maximize2Icon } from 'lucide-react'
import { memo } from 'react'
import { useSelector } from 'react-redux'

import { BeneficiaryLink } from '@/components/beneficiary/BeneficiaryLink'
import {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardFooter,
  CollapsibleCardHeader,
  CollapsibleCardLeftButtons,
  CollapsibleCardTitle,
  ItemCreatedDate,
  ItemUpdatedDate,
} from '@/components/CollapsibleCard'
import { TaskCategories, TaskDueDate, TaskLink } from '@/components/tasks/Task'
import { TaskList } from '@/components/tasks/TaskList'
import { useView } from '@/hooks'
import { selectDashboardTasks } from '@/store/dashboard-tasks/selectors'
import { TaskWithBeneficiary } from '@/store/dashboard-tasks/types'
import { useTaskLists } from '@/store/task'

export function Tasks() {
  const { tc } = useT()
  const tasks = useSelector(selectDashboardTasks)
  const { tasksComingSoon, tasksCount, tasksOverdue, tasksWithoutDueDate } =
    useTaskLists<TaskWithBeneficiary>(tasks)

  if (tasksCount === 0) return null
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle>{tc('tasks', { cfl: true })}</CardTitle>
        <CardDescription>
          {tc('task', {
            count: tasksCount,
          })}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-2">
        <TaskList
          tasks={tasksOverdue}
          transkey="overdue"
          variant="highlight"
          taskChildSlot={(task) => <InlineTask key={task.id} task={task} />}
        />

        <TaskList
          tasks={tasksComingSoon}
          transkey="comingSoon"
          taskChildSlot={(task) => <InlineTask key={task.id} task={task} />}
        />

        <TaskList
          tasks={tasksWithoutDueDate}
          transkey="noDueDate"
          taskChildSlot={(task) => <InlineTask key={task.id} task={task} />}
        />
      </CardContent>
    </Card>
  )
}

const InlineTask = memo(function ({
  className,
  task,
}: {
  className?: string
  task: TaskWithBeneficiary
}) {
  const { tc } = useT()
  const view = useView()
  return (
    <CollapsibleCard className={className}>
      <CollapsibleCardHeader>
        <CollapsibleCardLeftButtons>
          <TooltipWrapper content={tc('open', { cfl: true })}>
            <Button
              size="icon"
              variant="link"
              className="text-primary-300 hover:text-green-500"
              onClick={view.beneficiary(task.beneficiaryId).tasks.navigate}
            >
              <Maximize2Icon className="size-4" />
            </Button>
          </TooltipWrapper>
        </CollapsibleCardLeftButtons>
        <CollapsibleCardTitle>
          {task.title}{' '}
          <span className="text-xs text-primary-400">• {task.beneficiaryFullName}</span>
        </CollapsibleCardTitle>
      </CollapsibleCardHeader>

      <CollapsibleCardContent>
        <div className="flex items-center justify-between gap-2">
          <ItemCreatedDate createdAt={task.createdAt} />
          <ItemUpdatedDate updatedAt={task.updatedAt} />
          <TaskDueDate isEditable={false} initialDueDate={task.dueDate} />
        </div>

        <div className="whitespace-pre-wrap text-pretty">{task.content}</div>

        {<TaskLink task={task} />}

        {<TaskCategories task={task} />}

        <CollapsibleCardFooter className="flex-col-reverse sm:flex-row">
          <BeneficiaryLink id={task.beneficiaryId} fullName={task.beneficiaryFullName} />
        </CollapsibleCardFooter>
      </CollapsibleCardContent>
    </CollapsibleCard>
  )
})
